import moment from 'moment'
import React, { useState, useEffect, useContext } from 'react'
import injectStyles from 'react-jss'
import { styles } from './styles'
import reloadIcon from './assets/reload.svg'
import Select from '../../../../../Components/Inputs/Select'
import Input from '../../../../../Components/Inputs/Input'
import AvailabilitiesTable from './components/AvailabilitiesTable'
import useGraphQLFetch from '../../../../../Utils/Hooks/useGraphQLFetch'
import { getUsersWithAvailability } from './gql'
import LoadingIndicator from '../../../../../Components/LoadingIndicator/LoadingIndicator'
import { showReduxModal } from '../../../../../Reducers/UI/actions'
import { useDispatch } from 'react-redux'
import {
  getComparator,
  stableSort,
} from './components/AvailabilitiesTable/utils'
import {
  AVAILABILITY_FILTER_OPTIONS,
  AVAILABILITY_FILTER_VALUES,
} from './options'
import { convertArrayToHashMap } from '../../../../../shared/libraries/dataSets'
import {
  getTimeOfDayAndDayStringFromMissionDate,
  replaceGermanCharacters,
} from './utils'
import { FormContext } from '../../../../../Components/Form'

const Availabilities = ({
  classes,
  cityId,
  missionIdToShowDistance = null,
  bookBa = null,
  week: weekProps = null,
  callbackBrandAmbassadors = null,
  isRowExpanded = false,
  isLoadedCallback = null,
  isNeedRefetch,
  cityCheckInTimes,
}) => {
  const thisWeek = weekProps ? weekProps : moment().isoWeek()
  const nextWeek = !weekProps
    ? moment()
        .clone()
        .add(1, 'weeks')
        .isoWeek()
    : null

  const WEEKS_OPTIONS = [thisWeek, nextWeek].map(value => ({
    name: value,
    value: value,
  }))

  const [availabilities, setAvailabilities] = useState([])
  const [filteredAvailabilities, setFilteredAvailabilities] = useState([])
  const [initialAvailabilities, setInitialAvailabilities] = useState([])
  const [availabilityFilter, setAvailabilityFilter] = useState(
    AVAILABILITY_FILTER_VALUES.ALL,
  )
  const [week, setWeek] = useState(thisWeek)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState('desc')
  const [orderBy, setOrderBy] = useState('availableMissions')
  const [openRowIds, setOpenRowIds] = useState([])
  const [highligthedAvailability, setHighligthedAvailability] = useState(null)

  const { data, isLoading, isLoaded, refetch } = useGraphQLFetch({
    fetchFn: getUsersWithAvailability,
    defaultFetchArgs: { week, cityId, missionIdToShowDistance },
    mapResponse: data =>
      stableSort(
        data.getUserMissionAvailability,
        getComparator(order, orderBy),
      ),
  })

  const dispatch = useDispatch()

  const context = useContext(FormContext)

  const missionDateTime =
    context && context.data.dateTime ? context.data.dateTime.value : null
  useEffect(() => {
    if (isNeedRefetch) {
      refetch({ week: weekProps, cityId, missionIdToShowDistance })
    }
  }, [isNeedRefetch])

  useEffect(() => {
    if (weekProps) {
      refetch({ week: weekProps, cityId, missionIdToShowDistance })
    }
  }, [week, weekProps])

  useEffect(() => {
    if (isLoaded) {
      setAvailabilities(data)
      setFilteredAvailabilities(data)
      setInitialAvailabilities(data)
      if (isRowExpanded) {
        setOpenRowIds(data.map(({ userId }) => userId))
      }
      if (callbackBrandAmbassadors) {
        callbackBrandAmbassadors(convertArrayToHashMap([...data], 'userId'))
      }
      if (isLoadedCallback) {
        isLoadedCallback(isLoaded)
      }
    }
  }, [isLoaded])

  useEffect(() => {
    if (order && orderBy) {
      const filteredTableData = stableSort(
        filteredAvailabilities,
        getComparator(order, orderBy),
      )
      setFilteredAvailabilities(filteredTableData)
      setInitialAvailabilities(filteredTableData)
    }
  }, [orderBy, order])

  useEffect(() => {
    if (missionDateTime) {
      const { timeOfDay, day } = getTimeOfDayAndDayStringFromMissionDate({
        missionDate: missionDateTime,
        cityCheckInTimes,
      })
      setHighligthedAvailability({ timeOfDay, day })
    }
  }, [missionDateTime])

  const onReload = () => {
    refetch({ week, cityId, missionIdToShowDistance })
  }

  const onWeekChange = value => {
    setWeek(value)
    refetch({ week: value, cityId, missionIdToShowDistance })
  }

  const onAvailabilityChange = value => {
    setAvailabilityFilter(value)
    if (value === AVAILABILITY_FILTER_VALUES.ALL) {
      setFilteredAvailabilities(availabilities)
      setInitialAvailabilities(availabilities)
    } else {
      const [day, timeOfDay] = value.split('_')
      const filtered = availabilities.filter(availability => {
        const item =
          availability.accessibilityWithMissions.accessibility[day][timeOfDay]
        return item.isActive && !item.mission && !item.isTimeOff
      })
      setFilteredAvailabilities(filtered)
      setInitialAvailabilities(filtered)
    }
  }

  const ambassadorSearch = value => {
    setSearch(value)
    if (value) {
      const valueHasSpecialChars = /[äöüß]/.test(value)
      if (valueHasSpecialChars) {
        return setFilteredAvailabilities(
          initialAvailabilities.filter(item =>
            item.name.toLowerCase().includes(value.toLowerCase()),
          ),
        )
      }
      setFilteredAvailabilities(
        initialAvailabilities.filter(item =>
          replaceGermanCharacters(item.name)
            .toLowerCase()
            .includes(replaceGermanCharacters(value).toLowerCase()),
        ),
      )
    } else {
      setFilteredAvailabilities(initialAvailabilities)
    }
  }

  //TODO Not finished
  const handleBookBa = bookBa
    ? async ({ userId }) => {
        const userName = availabilities.find(
          availability => availability.userId === userId,
        ).name

        dispatch(
          showReduxModal({
            title: `Book Mission for ${userName}`,
            confirmLabel: 'Book',
            onConfirm: () => {
              alert('Booked')
            },
          }),
        )
      }
    : null

  const onRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const createSortHandler = property => event => {
    onRequestSort(event, property)
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.container}>
        <div className={classes.spaceBetween}>
          <div className={classes.title}>Brand Ambassadors</div>
          <div className={classes.reload}>
            <img src={reloadIcon} alt="icon" onClick={onReload} />
          </div>
        </div>
        <div className={classes.filtersWrapper}>
          {weekProps ? (
            <div className={classes.weekProps}>
              <div>Week</div>
              <div>{weekProps}</div>
            </div>
          ) : (
            <div className={classes.filter}>
              <Select
                label="Week"
                value={week}
                onChange={onWeekChange}
                options={WEEKS_OPTIONS}
                MenuProps={{ classes: { paper: classes.filter } }}
              />
            </div>
          )}
          <div className={classes.filter}>
            <Select
              label="Availability"
              value={availabilityFilter}
              onChange={onAvailabilityChange}
              options={AVAILABILITY_FILTER_OPTIONS}
              MenuProps={{ classes: { paper: classes.filter } }}
            />
          </div>
          <div className={classes.filter}>
            <Input
              label="Brand Ambassador"
              value={search}
              onChange={ambassadorSearch}
            />
          </div>
        </div>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <AvailabilitiesTable
            bookBa={handleBookBa}
            showDistance={missionIdToShowDistance}
            data={filteredAvailabilities}
            orderBy={orderBy}
            order={order}
            createSortHandler={createSortHandler}
            openRowIds={openRowIds}
            setOpenRowIds={setOpenRowIds}
            highligthedAvailability={highligthedAvailability}
          />
        )}
      </div>
    </div>
  )
}

export default injectStyles(styles)(Availabilities)
